
import { Component } from 'vue-property-decorator'
import { Constant } from '@/_services/constant'

// models
import TimeLineVariablesMixins from './TimeLineVariablesMixins.vue'
import { CalendarViewType } from '@/_enums/calendar_view'

// utils functions
import stc from 'string-to-color'
import moment from 'moment-timezone'
import tippy from 'tippy.js'
import { filterArrayWithOR } from '@/_helpers'
import { ResourceInterface } from '@/_interfaces'
import { RecordUserModel, RecordPlaceModel, RecordDestructUser, RecordDestructPlace } from '@/_models/AnalysisModel'
import BeaconModel from '@/_models/BeaconModel'
import { cloneDeep } from 'lodash'

@Component({
  components: {},
  computed: {},
})
export default class TimeLineMixins extends TimeLineVariablesMixins {
  convertFieldName(value: string): string | null {
    if (!value) {
      return null
    }
    return this.$t(this.filedNameLUT[value]) as string
  }

  getPlaceHolderText(field: string) {
    return `${this.$t('label.search_by_placeholder')} ${this.$t(this.filedNameLUT[field])}`
  }

  eventRender(info) {
    const description = info.event.extendedProps.description
    tippy(info.el, {
      content: description,
    })
  }

  stringToColor(text?: string): string {
    return stc(text)
  }

  getCalendarApi() {
    if (!this.$refs.calendar) {
      return null
    }

    return (this.$refs.calendar as any).getApi()
  }

  initFetch() {
    return Promise.all([
      this.$store.dispatch('users/getUsersRaw'),
      this.$store.dispatch('beacons/getAll', { beacon_type: Constant.BEACON_TYPE_ALL }),
      this.$store.dispatch('categories/getAll'),
    ])
  }

  stateDateChange(event) {
    const today = new Date()
    const isContainToday = event.start < today && today < event.end
    if (!isContainToday) {
      return this.hideIndicator()
    }

    this.clearInterval()
    this.initIndicator(event)

    this.intervalIndicator = setInterval(() => {
      this.initIndicator(event)
    }, 10000) // 10seconds reCalc position
  }

  initIndicator(event) {
    const totalSeconds = moment.duration(moment(event.end).diff(moment(event.start))).asSeconds()
    const fromTotalToNowSeconds = moment.duration(moment(new Date()).diff(moment(event.start))).asSeconds()
    const ratioFromStartToNow = fromTotalToNowSeconds / totalSeconds

    const scrollerEle = document.getElementsByClassName('fc-scrollgrid-sync-table')[1] as HTMLDivElement
    const minWidthScroll = +scrollerEle?.style?.minWidth?.replace('px', '')
    const widthScroll = +scrollerEle?.style?.width?.replace('px', '')
    const positionInTable = ratioFromStartToNow * (minWidthScroll > widthScroll ? minWidthScroll : widthScroll)

    this.drawIndicatorLine(positionInTable)
  }

  drawIndicatorLine(positionInTable: number) {
    const containers = document.getElementsByClassName('fc-timeline-now-indicator-container')
    if (containers.length) {
      const arrow = document.getElementsByClassName('fc-timeline-now-indicator-arrow')[0] as HTMLDivElement
      const line = document.getElementsByClassName('fc-timeline-now-indicator-line')[0] as HTMLDivElement

      arrow.style.left = `${positionInTable}px`
      line.style.left = `${positionInTable}px`
      return
    }

    this.createIndicator(positionInTable)
  }

  createIndicator(positionInTable: number) {
    const header = document.getElementsByClassName('fc-timeline-header')[0] as HTMLDivElement
    const body = document.getElementsByClassName('fc-timeline-body')[0] as HTMLDivElement

    header.appendChild(this.createContainerEle(positionInTable, 'arrow'))
    body.appendChild(this.createContainerEle(positionInTable, 'line'))
  }

  createContainerEle(positionInTable: number, childName: string) {
    const container = document.createElement('div')
    container.classList.add('fc-timeline-now-indicator-container')

    const childEle = document.createElement('div')
    childEle.classList.add(`fc-timeline-now-indicator-${childName}`)
    childEle.style.left = `${positionInTable}px`

    container.appendChild(childEle)

    return container
  }

  hideIndicator() {
    const containers = Array.from(document.getElementsByClassName('fc-timeline-now-indicator-container'))

    containers.forEach((box) => {
      box.remove()
    })

    this.clearInterval()
  }

  clearInterval() {
    if (this.intervalIndicator) {
      clearInterval(this.intervalIndicator)
    }
  }

  getDateToGo(dateViewType: string) {
    const isMonthView = this.currentDateView === CalendarViewType.month
    const isJumpDayView = dateViewType === CalendarViewType.day
    const isJumpWeekView = dateViewType === CalendarViewType.week
    // move from month to day same to week
    if (isMonthView && (isJumpDayView || isJumpWeekView)) {
      if (!this.lastDayToJump) {
        // special case. https://app.shortcut.com/sango-tech/story/18359/work-recorder-when-select-day-on-this-month-user-can-always-see-today https://app.shortcut.com/sango-tech/story/18359/work-recorder-when-select-day-on-this-month-user-can-always-see-today
        const isMatchCurrentMonth = new Date().getMonth() === new Date(this.pageFilters.dateFrom).getMonth()
        if (isMatchCurrentMonth && isMonthView && isJumpDayView) {
          return new Date()
        }

        const centerDayOfMonth = moment(this.pageFilters.dateFrom).set('date', 15).format(Constant.DATE_FORMAT_QUERY)
        return new Date(centerDayOfMonth)
      }

      return new Date(this.lastDayToJump)
    } // move from week to day
    else if (this.currentDateView === CalendarViewType.week && isJumpDayView) {
      const start = new Date(this.pageFilters.dateFrom)
      const end = new Date(this.pageFilters.dateTo)

      return new Date(+end - (+end - +start) / 2)
    }
  }

  calculatorCenPos(calendarApi: any) {
    const minutesToday = moment().minutes() + moment().hours() * 60
    const minutesOneDay = 24 * 60
    const currentPostion = minutesToday / minutesOneDay

    const calendarScrollHead = document.querySelector(
      '.fc-scrollgrid thead .fc-resource-timeline-divider~td .fc-scroller'
    ) as any
    const scrollGridTable = document.querySelector('.fc-timeline-header .fc-scrollgrid-sync-table') as any

    const centerPosInScroll = scrollGridTable.offsetWidth * currentPostion - calendarScrollHead.offsetWidth / 2
    const scrollTimeTotalMinutes = Math.round((centerPosInScroll / scrollGridTable.offsetWidth) * minutesOneDay)
    const scrollTimeHours = Math.floor(scrollTimeTotalMinutes / 60)
    const scrollTimeMinutes = scrollTimeTotalMinutes - scrollTimeHours * 60
    const scrollTimesMinPrefix = scrollTimeMinutes.toString().length === 1 ? '0' : ''
    const scrollTimeParse = `${scrollTimeHours}:${scrollTimesMinPrefix}${scrollTimeMinutes}:00`

    calendarApi.scrollToTime(scrollTimeParse)
  }

  async getCardStatic() {
    await this.$store.dispatch('cards/getAll', {
      card_type: Constant.CARD_TYPE_STATIC,
    })
    const cardStatic = this.$store.state.cards.all.items as any[]
    const combineDevices = this.beacons.concat(cardStatic)
    this.beacons = combineDevices
  }

  isSameTodayWithRange(dateFrom: string, dateTo: string): boolean {
    const toDayStr = moment().format(Constant.DATE_FORMAT_QUERY)
    return dateFrom === dateTo && dateFrom === toDayStr
  }

  resetFiltersData(selectFilter: string = 'u_name') {
    this.filtersData = {
      searchQuery: '',
      selectedFilter: selectFilter,
    }
  }

  getBeaconByMiMn(major?: string, minor?: string, locationPlace?: string): BeaconModel {
    let beaconFilter: any = null
    if (major && minor) {
      beaconFilter = this.beacons.find((beacon) => beacon.major === major && beacon.minor === minor) as BeaconModel
    }

    if (!beaconFilter) {
      beaconFilter = this.beacons.find(
        (beacon) => `${beacon.floor_name}-${beacon.location_name}` === locationPlace
      ) as BeaconModel
    }

    return beaconFilter
  }

  getUserHoldingDevice(sanId?: string, userName?: string) {
    let userFound: any
    if (sanId) {
      userFound = this.users.find((user) => (user.san_id || user.beacon_id) === sanId) as any
    }
    if (!userFound && userName) {
      userFound = this.users.find((user) => user.name === userName)
    }
    return userFound
  }

  destructingRecords(records: Array<RecordUserModel | RecordPlaceModel>) {
    const destructedRecords: Array<RecordDestructUser | RecordDestructPlace> = []

    records.forEach((item: RecordUserModel | RecordPlaceModel) => {
      const { its, ...itemInfo } = item
      its.forEach((it) => {
        destructedRecords.push({
          ...it,
          ...itemInfo,
        })
      })
    })

    return destructedRecords
  }

  handleFilterResource(array: ResourceInterface[], query: string): ResourceInterface[] {
    if (!query && !query.trim().length) {
      return array
    }

    const filters = {
      [this.filtersData.selectedFilter]: (value: string) => value?.toLowerCase().indexOf(query.toLowerCase()) >= 0,
    }

    return filterArrayWithOR(cloneDeep(array), filters)
  }

  clearFetchTimeOut() {
    if (this.timeOut) {
      clearTimeout(this.timeOut)
      this.timeOut = null
    }
  }

  beforeDestroy() {
    this.hideIndicator()

    this.clearFetchTimeOut()
    this.filterUtilPage.save(this.pageFilters)

    this.$store.dispatch('resetAllModulesState', ['analysis'])
  }
}
